// Screen · Classes (route /classes) — 2026-09-30 redesign.
//   Teacher: their own classes (open one for its student list and lessons) + "Add a class".
//   Lead:    the class manager (every class, its teacher, periods per week, add a class FOR a
//            teacher) — the same component as Setup › Classes — plus their own "Add a class".
// Empty state is encouraging (Design Brief).

import { api } from "../api";
import { esc } from "../dom";
import { t } from "../i18n";
import { icon } from "../icons";
import { me } from "../state";
import { byGrade } from "../lesson-data";
import { pageHeader, emptyState } from "../ui";
import { mountClassManager } from "./class-manager";

interface Cls {
  id: string;
  name: string;
  level: string | null;
  teacher_id: string;
  teacher_name?: string | null;
  periods_per_week: number;
}

export async function renderClasses(app: HTMLElement): Promise<void> {
  const isLead = me()?.auth_role === "lead";
  const formBody = `
        <label for="cname">${t("classes.name")}<input id="cname" placeholder="G4A" required maxlength="40" /></label>
        <label for="clevel">${t("classes.level")}<input id="clevel" placeholder="Movers" /></label>
        <p class="hint span-2">Start the name with the grade (for example “G4A”) so EDEN can find the class's year plan.</p>
        <div id="add-err" class="error span-2" hidden></div>
        <div class="form-actions span-2"><button class="btn" type="submit">${t("classes.add_button")}</button></div>`;
  // A teacher's own form stays open (short, and the only way to add a class before the Lead does).
  // The Lead adds every class — their own included — with the class manager's "Add a class" (it
  // asks for the teacher), so there is one way to do it, not two.
  // A teacher with no class yet sees the form open; one who has classes finds it in a fold (the Lead
  // normally sets classes up — this stays for the day a class is missing).
  const ownNow = isLead ? [] : (await api<{ classes: Cls[] }>("/api/classes?scope=own")).classes;
  const addForm = isLead
    ? ""
    : ownNow.length
      ? `<details class="card fold" id="add-own-fold">
          <summary>${icon("plus")}<span>A class is missing? Add it here — or ask your Lead</span>${icon("down", "ico-sm fold-caret")}</summary>
          <form id="add" class="fold-body form-grid">${formBody}</form>
        </details>`
      : `<section class="card form-card" aria-labelledby="add-h">
          <h2 class="card-title" id="add-h">${t("classes.add")}</h2>
          <form id="add" class="form-grid">${formBody}</form>
        </section>`;

  app.innerHTML = `
    ${pageHeader({
      title: isLead ? "Classes" : t("nav.my_classes"),
      sub: isLead ? "Every class in the department, its teacher and its periods per week." : "Open a class for its student list and its lessons.",
    })}
    <p class="hint"><a data-link href="/admin/data-handling">How private class results are handled</a>${isLead ? ` · <a data-link href="/admin/gradebook">Marks setup: year dates and marking rules</a>` : ""}</p>
    <div id="list"></div>
    ${addForm}`;

  let own: Cls[] = [];
  async function load(): Promise<void> {
    const list = app.querySelector<HTMLElement>("#list")!;
    own = (await api<{ classes: Cls[] }>("/api/classes?scope=own")).classes;
    if (isLead) {
      await mountClassManager(list);
      return;
    }
    const sorted = [...own].sort(byGrade);
    list.innerHTML = sorted.length
      ? `<div class="class-cards">${sorted.map((c) => `<a class="class-card card" data-link href="/classes/${esc(c.id)}" data-class-id="${esc(c.id)}">
            <span class="class-badge big">${esc(c.name)}</span>
            <span class="cc-main"><span class="cc-level">${esc(c.level ?? "")}</span>
            <span class="muted small">${c.periods_per_week} periods per week</span></span>
            ${icon("chevron", "ico-sm")}
          </a>`).join("")}</div>`
      : emptyState(t("classes.empty"));
  }

  const addErr = app.querySelector<HTMLElement>("#add-err");
  app.querySelector<HTMLFormElement>("#add")?.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!addErr) return;
    addErr.hidden = true;
    const name = app.querySelector<HTMLInputElement>("#cname")!.value.trim();
    const level = app.querySelector<HTMLInputElement>("#clevel")!.value.trim();
    if (!name) return;
    // Gentle guard: block a case-insensitive duplicate before the round-trip (the server enforces
    // this too). Calm message, never an alarm.
    if (own.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      addErr.textContent = `You already have a class called ${name}.`;
      addErr.hidden = false;
      return;
    }
    try {
      await api("/api/classes", { method: "POST", body: JSON.stringify({ name, level }) });
    } catch (ex) {
      addErr.textContent = (ex as Error)?.message ?? "Couldn't add the class — please try again.";
      addErr.hidden = false;
      return;
    }
    app.querySelector<HTMLFormElement>("#add")!.reset();
    await load();
  });

  await load();
}
